import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal, IonText } from "@ionic/react";
import { Helmet } from "react-helmet";

const objects = [
    { emoji: "🚗", position: "Front" },
    { emoji: "🐕", position: "Front" },
    { emoji: "🚲", position: "Front" },
    { emoji: "🌲", position: "Back" },
    { emoji: "🌳", position: "Back" },
    { emoji: "🌴", position: "Back" },
    { emoji: "⛰️", position: "Back" },
    { emoji: "🗼", position: "Back" },
];

const fixedObject = ["🏠", "🏕️", "🏰", "🏝️"];

const FrontBackGame: React.FC = () => {
    const [question, setQuestion] = useState<{ emoji: string; position: string }>({
        emoji: "",
        position: "",
    });

    const [showFeedback, setShowFeedback] = useState<boolean | null>(null);
    const [fixedEmoji, setFixedEmoji] = useState('');
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Front and Back Worksheets for UKG | Spatial Position Concept";
    }, []);

    const generateNewQuestion = () => {
        const randomObj = objects[Math.floor(Math.random() * objects.length)];
        const randomFixedObject = fixedObject[Math.floor(Math.random() * fixedObject.length)]; console.log(randomFixedObject)
        setFixedEmoji(randomFixedObject)
        setQuestion(randomObj);
        setShowFeedback(null);
    };

    const handleAnswer = (answer: string) => {
        if (answer === question.position) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setShowFeedback(false)
            }, 5000)
        } else {
            setShowImage(false);
            setShowFeedback(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG kids the concept of front and back through fun worksheets and visual cues. Improve spatial understanding with printable activities today!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/front-back" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord heavyLight matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Front and Back</h1>

                                    <p>
                                        The concept of <strong>front</strong> and <strong>back</strong> introduces young children to spatial relationships. It’s a foundational skill in pre-math learning that helps UKG students describe object positions in relation to each other. Our <strong>front and back worksheets</strong> use colorful visuals and fun examples to make learning engaging.
                                    </p>

                                    <h2>🚸 What Do Front and Back Mean?</h2>
                                    <p>
                                        “Front“ is the side that faces forward or is ahead, while “Back“ is the side that trails behind or faces away. Children learn this by observing real-life objects like:
                                        <ul>
                                            <li>The <strong>front</strong> of a car has headlights; the <strong>back</strong> has taillights.</li>
                                            <li>The <strong>front</strong> of a book has the cover; the <strong>back</strong> has the blurb.</li>
                                        </ul>
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand spatial vocabulary: front, back</li>
                                        <li>Identify front and back in objects and pictures</li>
                                        <li>Use real-world contexts to describe positions</li>
                                        <li>Build visual reasoning and direction-following skills</li>
                                    </ul>

                                    <h2>🖍️ Activities in the Worksheets</h2>
                                    <ul>
                                        <li>Circle the object at the front/back</li>
                                        <li>Match front and back views of common items</li>
                                        <li>Identify people standing in front of or behind others</li>
                                        <li>Complete scenes by placing items at the front or back</li>
                                        <li>Color objects based on their position</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                    <ul>
                                        <li>Use toys or stuffed animals to show position: <strong>“Who is at the front?”</strong></li>
                                        <li>Practice in queues: “You are standing at the back/front of the line.”</li>
                                        <li>Use books to show the difference between cover (front) and back</li>
                                        <li>Play hide and seek using spatial language: “Look behind the chair.”</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Let your child lead a line of toys and ask, <strong>“Which toy is at the front? Who’s at the back?”</strong> It adds movement to learning and builds confidence in spatial concepts.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Front and Back</h1>

                                <p>
                                    The concept of <strong>front</strong> and <strong>back</strong> introduces young children to spatial relationships. It’s a foundational skill in pre-math learning that helps UKG students describe object positions in relation to each other. Our <strong>front and back worksheets</strong> use colorful visuals and fun examples to make learning engaging.
                                </p>

                                <h2>🚸 What Do Front and Back Mean?</h2>
                                <p>
                                    “Front“ is the side that faces forward or is ahead, while “Back“ is the side that trails behind or faces away. Children learn this by observing real-life objects like:
                                    <ul>
                                        <li>The <strong>front</strong> of a car has headlights; the <strong>back</strong> has taillights.</li>
                                        <li>The <strong>front</strong> of a book has the cover; the <strong>back</strong> has the blurb.</li>
                                    </ul>
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand spatial vocabulary: front, back</li>
                                    <li>Identify front and back in objects and pictures</li>
                                    <li>Use real-world contexts to describe positions</li>
                                    <li>Build visual reasoning and direction-following skills</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Circle the object at the front/back</li>
                                    <li>Match front and back views of common items</li>
                                    <li>Identify people standing in front of or behind others</li>
                                    <li>Complete scenes by placing items at the front or back</li>
                                    <li>Color objects based on their position</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Use toys or stuffed animals to show position: <strong>“Who is at the front?”</strong></li>
                                    <li>Practice in queues: “You are standing at the back/front of the line.”</li>
                                    <li>Use books to show the difference between cover (front) and back</li>
                                    <li>Play hide and seek using spatial language: “Look behind the chair.”</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Let your child lead a line of toys and ask, <strong>“Which toy is at the front? Who’s at the back?”</strong> It adds movement to learning and builds confidence in spatial concepts.
                                </p>
                            </article>
                            <div className="" style={{ marginBottom: "5rem" }}>
                                <h2>Where is the {question.emoji}?</h2>
                            </div>

                            <div className="mt-2" style={{ position: "relative", display: "inline-block", margin: "20px", marginBottom: "2rem" }}>
                                {/* Back Object - Slightly Bigger */}
                                {question.position === "Back" && (
                                    <span style={{ fontSize: "140px", position: "absolute", top: "-90px", left: "0", opacity: "0.6" }}>
                                        {question.emoji}
                                    </span>
                                )}

                                {/* Center House - Standard Size */}
                                <span style={{ fontSize: "100px", position: "relative", zIndex: 1 }}>{fixedEmoji}</span>

                                {/* Front Object - Slightly Smaller */}
                                {question.position === "Front" && (
                                    <span style={{ fontSize: "60px", position: "absolute", bottom: "-25px", left: "20px", zIndex: "9" }}>
                                        {question.emoji}
                                    </span>
                                )}
                            </div>

                            <div>
                                <IonButton onClick={() => handleAnswer("Front")} color="primary">
                                    Front
                                </IonButton>
                                <span style={{ marginRight: "20px", marginLeft: "20px" }}>or</span>
                                <IonButton onClick={() => handleAnswer("Back")} color="secondary">
                                    Back
                                </IonButton>
                                <span style={{ marginRight: "20px", marginLeft: "20px" }}>of</span> <span>{fixedEmoji}</span>
                            </div>

                            {showFeedback !== null && (
                                <IonText>
                                    <h3 style={{ color: showFeedback ? "green" : "red" }}>
                                        {showFeedback ? "Correct! 🎉" : "Try Again! ❌"}
                                    </h3>
                                </IonText>
                            )}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default FrontBackGame;
